/**
 * A time, rendered the way times should be: relative on screen, absolute in the
 * tooltip.
 *
 * "4 minutes ago" is what you want when scanning an audit log for what just
 * happened. "16 Sept 2026, 09:14" is what you want the moment something is
 * actually wrong and you are comparing it against a server log. Showing one
 * without the other picks a side; this pairing does not.
 *
 * It calls `useTranslation()` without using `t` for anything but the em dash.
 * That is not redundant — it is the **subscription**. The words come out of
 * `utils/format`, which reads the language from the i18next singleton rather
 * than from React, so without this hook a language change would leave every
 * timestamp on screen in the old language until something else re-rendered it.
 */
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { formatDateTime, formatRelative } from '../utils/format';

export interface TimestampProps {
  /** ISO-8601, as every API timestamp is. `null` renders an em dash. */
  value: string | null | undefined;
  /** Shows the absolute time inline instead, keeping the relative one in the tooltip. */
  absolute?: boolean;
}

export function Timestamp({ value, absolute = false }: TimestampProps) {
  const { t } = useTranslation('common');

  if (!value) return <Box component="span">{t('state.empty')}</Box>;

  const relative = formatRelative(value);
  const exact = formatDateTime(value);

  return (
    <Tooltip title={absolute ? relative : exact} placement="top" disableInteractive>
      {/* whiteSpace: a wrapped "2 minutes\nago" in a table column reads badly. */}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {absolute ? exact : relative}
      </Box>
    </Tooltip>
  );
}
